<template>
  <div class="flex flex-col justify-center items-center gap-3 min-w-[448px]">
    <div class="w-full flex justify-center items-end">
      <div class="form-control grow">
        <label class="label pl-3">
          <span class="label-text">Name</span>
        </label>

        <input
          type="text"
          id="config-name"
          ref="configName"
          placeholder="New name"
          v-model="inputName"
          class="input input-bordered w-full"
          :disabled="disabled"
        />
      </div>

      <div
        class="form-control pb-3 pl-10 pr-8 tooltip"
        data-tip="Make this configuration visible for other users"
      >
        <label class="label mb-1">
          <span class="label-text">Public</span>
        </label>

        <input type="checkbox" class="toggle" v-model="inputPublic" />
      </div>
    </div>

    <div class="form-control w-full">
      <label class="label pl-3">
        <span class="label-text">Description</span>
      </label>
      <textarea
        class="textarea textarea-bordered h-60"
        placeholder="Enter description"
        :disabled="disabled"
        v-model="inputDescription"
      ></textarea>
    </div>

    <div
      class="w-full pt-4 px-6 flex justify-center items-center gap-3 flex-wrap md:flex-nowrap"
    >
      <ConfigUpDownloadButton class="tour-config-updownload" />
      <ConfigDeleteButton />
      <ConfigNewButton />
      <ConfigUpdateButton />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useBackendStore } from "@/stores";
import ConfigUpDownloadButton from "@/components/config/ConfigUpDownloadButton.vue";
import ConfigDeleteButton from "@/components/config/ConfigDeleteButton.vue";
import ConfigUpdateButton from "@/components/config/ConfigUpdateButton.vue";
import ConfigNewButton from "@/components/config/ConfigNewButton.vue";

const backend = useBackendStore();

const configName = ref<HTMLElement | null>(null);

// Use separate refs so that it will work even when currentConfig is still null
const inputPublic = ref(backend.currentConfig?.public ?? true);
const inputName = ref("");
const inputDescription = ref(backend.currentConfig?.description ?? "");
watch(inputPublic, (v) => {
  if (backend.currentConfig != null) backend.currentConfig.public = v;
});
watch(inputName, (v) => {
  if (backend.currentConfig != null && v != "") backend.currentConfig.name = v;
});
watch(inputDescription, (v) => {
  if (backend.currentConfig != null && v != "")
    backend.currentConfig.description = v;
});

// Make sure we know which user is logged in
backend.fetchUser();

const disabled = computed(() => backend.currentConfig == null);

// Synchronize local state of public switch on new config
watch(
  () => backend.currentConfig?.id,
  () => {
    if (backend.currentConfig != null) {
      inputPublic.value = backend.currentConfig.public;
      inputDescription.value = backend.currentConfig.description;
    }
  }
);

// Synchronize value of name input field
watch(
  () => ({ id: backend.currentConfig?.id, name: backend.currentConfig?.name }),
  (newVal, old) => {
    if (configName.value == null) return;
    const configChanged = newVal.id != old.id;
    const nameChanged = newVal.name != old.name;

    if (nameChanged && !configChanged && newVal.name != undefined) {
      // Other component forced new config name, update input
      inputName.value = newVal.name;
    } else if (configChanged) {
      // Clear input when new predefined config is selected
      inputName.value = "";
    }
  }
);
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  @apply transition-all duration-200;
}

.v-enter-from,
.v-leave-to {
  @apply opacity-0 scale-50;
}
</style>
